import React, { useEffect, useRef, useState } from 'react';
import './index.module.less';

import userStore from '@stores/user';
import { Api } from '@apis/client';
import Http from '@helpers/Http';
import Filter from '@helpers/Filter';
import NativeInterface from '@helpers/NativeInterface';
import {
  isCubeApp,
  commandMessage,
  locationType,
  defaultPoint,
  deviceStatus,
  paths,
} from '@helpers/Constants';
import { transformToBd09 } from '@utils/mapUtil';
import FridayService from '@services/friday';
import { getNativeProxySington } from '@helpers/NativeProxy';
import BrowserHistory from '@helpers/BrowserHistory';
import callUtils from '@utils/callUtil';
import * as Cache from '@helpers/Cache';

import NavBar from '@components/NavBar';
import BaseMap, { CustomOverlay } from '@components/map/Map/BaseMap';

import Toast from '@widgets/Toast';
import Alert from '@widgets/Alert';

import Card from './Card';
// import ItemDevice from './ItemDevice';
import Empty from './Empty';
import Tips from './Tips';
import defaultAvatar from '@images/avatar_default.png';
import OfflineHelp from '@components/OfflineHelp';

import LocationPopWindow from './LocationPopWindow';
import ConfirmModal from '@components/ConfirmModal';
import DeviceRemindAuth from './DeviceRemindAuth';
import ChildrenSelectMenu from './ChildrenSelectMenu';

//首次进入时显示历史定位信息
//点击刷新定位后 显示定位获取中，间隔8s请求一次定位信息，比较当前获取的定位时间与上次时间差是否小于一分钟，小于一分钟则认为是获取到的最新定位
//切换设备时，如果是在定位轮询请求中，清除定时请求，再获取当前设备定位
//假进度条的状态：
//0-30 定位下发 5s
//30-60 定位获取 10s
//60-99 定位进度显示 10s
//99-100 根据获取状态显示

const Location = () => {
  const timerId = useRef(null);
  const params = useRef(Filter.urlParams(window.location.search));
  const isRefreshData = useRef<boolean>(false);
  const intervalTimer = useRef(null);
  const currentDevice = useRef(null);
  const pollingCount = useRef(0);
  const mapRef = useRef(null);
  const CardComponent = useRef<any>(null);

  const [devices, setDevices] = useState(null);
  const [center, setMapCenter] = useState(defaultPoint);
  const [deviceInfo, setDeviceDetail] = useState(null);
  const [devicePosition, setDevicePosition] = useState(null);
  const [showAnimation, setAnimation] = useState(false);
  const [errInfo, setErrInfo] = useState(null);
  const [contentText, setContentText] = useState('');
  const [percentCount, setPercentCount] = useState(0);
  const [displayLocationPopWindow, setDisplayLocationPopWindow] = useState(
    false,
  );
  const [deviceSNInfo, setDeviceSNInfo] = useState(null);
  const [messageNum, setMessageNum] = useState<number>(0);
  const [hasRemindAuth, setRemindAuth] = useState(isCubeApp() ? true : false);
  const [showMenu, setShowMenu] = useState(false);
  const [showPlaceHint, setShowPlaceHint] = useState(false);
  const call = () => {
    if (deviceInfo.status === deviceStatus.locked) {
      return ConfirmModal.show({
        title: '设备已锁定',
        text:
          '设备插入的SIM卡不是服务商提供，因此设备已被锁定，解锁请插入服务商提供的SIM卡，并确认SIM卡可正常使用。',
        confirmBtnText: '我知道了',
      });
    }
    if (deviceInfo.status === deviceStatus.forbid) {
      return ConfirmModal.show({
        title: '设备已禁用',
        text: '设备已被服务商禁用，请联系服务商解除。',
        confirmBtnText: '我知道了',
        confirmBtnStyle: { backgroundColor: '#F2F4F7', color: '#55585C' },
      });
    }
    if (isCubeApp()) {
      NativeInterface.postMessage(commandMessage.cardCall, {
        snCode: currentDevice.current.snCode,
        phone: currentDevice.current.phone,
      });
    } else {
      callUtils.call(currentDevice.current.snCode || '');
    }
    FridayService.addEvent('family_call_dail_click', { source: '实时定位' });
  };

  const onRefresh = async () => {
    if (deviceInfo.status === deviceStatus.locked) {
      return ConfirmModal.show({
        title: '设备已锁定',
        text:
          '设备插入的SIM卡不是服务商提供，因此设备已被锁定，解锁请插入服务商提供的SIM卡，并确认SIM卡可正常使用。',
        confirmBtnText: '我知道了',
      });
    }
    if (deviceInfo.status === deviceStatus.forbid) {
      return ConfirmModal.show({
        title: '设备已禁用',
        text: '设备已被服务商禁用，请联系服务商解除。',
        confirmBtnText: '我知道了',
        confirmBtnStyle: { backgroundColor: '#F2F4F7', color: '#55585C' },
      });
    }
    const instanceMap = mapRef.current;
    instanceMap.setViewport();
    if (isRefreshData.current) {
      return;
    }
    isRefreshData.current = true;
    getDeviceDetail(currentDevice.current, true);
  };

  //获取设备信息
  const checkDeviceOnline = async (value: any) => {
    try {
      const { data } = await Http.post(Api.GetCardInfoBySncode, {
        snCode: value?.snCode,
        company: currentDevice.current.company,
      });
      setDeviceDetail(data);
      if (!data?.isOnLine) {
        Toast.show({
          type: 'strip',
          title: '设备离线，请联网后设置',
        });
        setMapCenter(defaultPoint);
        setErrInfo({
          title: '设备离线，无法获取当前定位',
          clickEvent: () => {
            OfflineHelp.show();
          },
        });
      }
      return data;
    } catch (error) {}
  };

  const checkLocationSwitchStatus = async (value: any) => {
    const { data } = await Http.post(Api.GetDeviceSwitchInfo, {
      snCode: value?.snCode,
      company: currentDevice.current.company,
      key: 'locationSwitch',
    });

    const enabled = data === '1';
    setDeviceSNInfo({
      snCode: value?.snCode,
      company: currentDevice.current.company,
    });

    if (!enabled) {
      setMapCenter(defaultPoint);
      setErrInfo({
        title: '定位未开启，暂无定位信息，前往开启定位',
        clickEvent: () => {
          FridayService.addEvent(
            'real_time_positioning_more_positioning_settings_display',
            { source: '未开启定位服务通知栏' },
          );
          BrowserHistory.push(
            `${PUBLIC_URL}/locationsetting?sncode=${value?.snCode}&company=${currentDevice.current.company}`,
          );
        },
      });
      setDisplayLocationPopWindow(true);
    }

    return enabled;
  };

  //假进度条的定时增加数字逻辑 0-99
  const calcInterval = () => {
    intervalTimer.current = setInterval(() => {
      setPercentCount(val => {
        const newVal = val + 1;
        if (newVal >= 99) {
          clearInterval(intervalTimer.current);
        }
        return newVal;
      });
    }, 200);
  };

  const getDeviceDetail = async (value: any, isRefresh = false) => {
    setErrInfo(null);
    setAnimation(true);
    const data = await checkDeviceOnline(value);
    if (data?.status === deviceStatus.locked) {
      setErrInfo({
        title: '设备已锁定，请检查锁定原因',
        clickEvent: () => {
          ConfirmModal.show({
            title: '设备已锁定',
            text:
              '设备插入的SIM卡不是服务商提供，因此设备已被锁定，解锁请插入服务商提供的SIM卡，并确认SIM卡可正常使用。',
            confirmBtnText: '我知道了',
          });
        },
      });
      setAnimation(false);
      return;
    } else if (data?.status === deviceStatus.forbid) {
      setErrInfo({
        title: '设备已禁用，请检查禁用原因',
        clickEvent: () => {
          ConfirmModal.show({
            title: '设备已禁用',
            text: '设备已被服务商禁用，请联系服务商解除。',
            confirmBtnText: '我知道了',
            confirmBtnStyle: { backgroundColor: '#F2F4F7', color: '#55585C' },
          });
        },
      });
      setAnimation(false);
      return;
    }
    if (!data?.isOnLine) {
      setAnimation(false);
      isRefreshData.current = false;
      return;
    }

    const isLocationSwitchOpen = await checkLocationSwitchStatus(value);
    if (!isLocationSwitchOpen) {
      setAnimation(false);
      isRefreshData.current = false;
      return;
    }

    await Http.post(Api.PostPositionV1Realtime, {
      snCode: value?.snCode,
    }).catch(() => {
      setAnimation(false);
    });

    //获取设备实时定位
    const { data: result } = await Http.post(Api.GetPositionV1Realtime, {
      snCode: value?.snCode,
    });
    if (isRefresh) {
      calcInterval();
      if (result) {
        //比较当前时间与现在的时间差是否小于1分钟，小于一分钟则认为是最新的定位
        if (result?.timestamp && Date.now() - result.timestamp <= 60 * 1000) {
          isRefreshData.current = false;
          setContentText('获取定位成功');
          intervalTimer.current && clearInterval(intervalTimer.current);
          setTimeout(() => setContentText(''), 2000);
          setAnimation(false);
          updatePositionInfo(result);
          FridayService.addEvent(
            'real_time_positioning_update_return_results_show',
            { type: '成功' },
          );
        } else {
          pollingPosition();
        }
      } else {
        pollingPosition();
      }
    } else {
      setAnimation(false);
      if (result) {
        if (data?.isOnLine) {
          Toast.show({
            type: 'strip',
            title: '定位更新成功',
          });
        }
        updatePositionInfo(result);
      } else {
        setMapCenter(defaultPoint);
        const instanceMap = mapRef.current;
        instanceMap.setCenter(defaultPoint);
      }
    }
  };

  //地址解析
  const transferPosition = async (data: any) => {
    //如果后台有返回address 那么直接用
    if (data.address) {
      setDevicePosition(data);
    } else {
      //没有返回详细地址时，需要解析地址
      if (data.longitude && data.latitude) {
        const res = await mapRef.current.getLocation(
          defaultPoint || { lng: data.longitude, lat: data.latitude },
        );
        setDevicePosition({
          ...data,
          address: (res.address || '') + (res.title || ''),
        });
      }
    }
  };

  //获取定位成功后更新定位信息
  const updatePositionInfo = (result: any) => {
    const point = transformToBd09({ lng: result.longitude, lat: result.latitude });
    setMapCenter(point);
    transferPosition(result);
    const instanceMap = mapRef.current;
    instanceMap.setViewport({
      points: [point],
      margins: [100, 100, 200, 200],
    });
    if (result?.locationType === locationType.lbs) {
      setErrInfo({ title: '当前为模糊定位，误差较大，仅供参考' });
    } else {
      setErrInfo(null);
    }
  };

  //间隔5s请求一次定位接口
  const pollingPosition = async () => {
    clearTimer();
    timerId.current = setInterval(async () => {
      if (pollingCount.current > 8) {
        isRefreshData.current = false;
        setContentText('获取定位失败');
        setAnimation(false);
        setPercentCount(0);
        pollingCount.current = 0;
        intervalTimer.current && clearInterval(intervalTimer.current);
        setTimeout(() => {
          setContentText('');
        }, 2000);
        clearTimer();
        FridayService.addEvent(
          'real_time_positioning_update_return_results_show',
          { type: '失败' },
        );
        return;
      }

      const response = await Http.post(
        Api.GetPositionV1Realtime,
        { snCode: currentDevice.current?.snCode },
        { noLoading: true },
      ).catch(err => {
        // eslint-disable-next-line
        console.log(err, 'err');
      });

      const result = response ? response?.data : {};
      if (result?.timestamp && Date.now() - result.timestamp <= 60 * 1000) {
        setContentText('获取定位成功');
        setTimeout(() => {
          setContentText('');
        }, 2000);
        setPercentCount(0);
        setAnimation(false);
        intervalTimer.current && clearInterval(intervalTimer.current);
        updatePositionInfo(result);
        isRefreshData.current = false;
        pollingCount.current = 0;
        FridayService.addEvent(
          'real_time_positioning_update_return_results_show',
          { type: '成功' },
        );
        clearTimer();
      } else {
        //轮询间隔请求时间设置为5s请求一次  总请求时间为40s，若在当前页面一直未请求到数据，则显示定位失败,定位数据设置为之前的旧定位数据
        if (pollingCount.current > 8) {
          setAnimation(false);
          setContentText('获取定位失败');
          intervalTimer.current && clearInterval(intervalTimer.current);
          setTimeout(() => {
            isRefreshData.current = false;
            setContentText('');
            setPercentCount(0);
            result && updatePositionInfo(result);
          }, 2000);
          pollingCount.current = 0;
          clearTimer();
          FridayService.addEvent(
            'real_time_positioning_update_return_results_show',
            { type: '失败' },
          );
        }
        pollingCount.current++;
      }
    }, 5000);
  };

  const clearTimer = () => {
    if (timerId.current) {
      clearInterval(timerId.current);
    }
  };

  const getFrequentedPlace = async (childrenData: any) => {
    const { data } = await Http.post(Api.GetMobileRegionStayV1, {
      snCode: childrenData.snCode,
      company: childrenData.company,
    });
    if (data.length) {
      setShowPlaceHint(false);
    } else {
      setShowPlaceHint(true);
    }
  };

  const getDeviceInfo = async () => {
    const { data: deviceList } = await Http.post(Api.GetDevicesByUserUid, {
      userUid: userStore.userInfo.uid,
    });
    if (!deviceList?.length) {
      setDevices([]);
      return;
    }
    //获取用户设备列表
    //当前如果有选中的设备，判断是否在当前管理的设备列表中
    //没有选中的设备，默认选中第一个作为当前使用设备
    const currentDeviceExist =
      currentDevice.current &&
      deviceList.find(
        (item: any) => item.snCode === currentDevice.current.snCode,
      );
    let selectDevice = localStorage.getItem('select_device')
      ? JSON.parse(localStorage.getItem('select_device'))
      : '';
    let index = deviceList.findIndex(
      (item: any) => item.snCode === selectDevice.snCode,
    );

    let currentSelectDevice = currentDeviceExist
      ? currentDeviceExist
      : index > -1
      ? selectDevice
      : deviceList[0];

    const cacheSnCode = Cache.get('map-sncode');
    if (params.current?.sncode || cacheSnCode) {
      const snCode = params.current?.sncode || cacheSnCode;
      //如果链接有带sncode 证明是查询某个设备定位
      const findDevice = deviceList.find((item: any) => item.snCode === snCode);

      if (!findDevice) {
        return Alert.show('您没有绑定该设备');
      }
      currentSelectDevice = findDevice;
    }
    currentDevice.current = currentSelectDevice;
    getFrequentedPlace(currentSelectDevice);
    setDevices(deviceList);
    getDeviceDetail(currentSelectDevice);
  };

  const selectDevice = async (value: any) => {
    if (timerId.current) {
      clearTimer();
    }
    //切换设备时，将位置信息和设备电量信息等清除
    if (value.snCode !== currentDevice.current?.snCode) {
      localStorage.setItem('select_device', JSON.stringify(value));
      FridayService.addEvent('real_time_positioning_switch_device_click');
      setDevicePosition(null);
      setDeviceDetail(null);
      currentDevice.current = value;
      if (isRefreshData.current) {
        FridayService.addEvent(
          'real_time_positioning_update_return_results_show',
          { type: '中断' },
        );
        isRefreshData.current = false;
        setContentText('');
        intervalTimer.current && clearInterval(intervalTimer.current);
        setPercentCount(0);
      }
      getFrequentedPlace(value);
      getDeviceDetail(value);
    }
  };

  // const renderDevices = () => {
  //   if (devices.length <= 1) {
  //     return null;
  //   }
  //   console.log(devices, deviceInfo);
  //   let Items = devices.map((item: any) => {
  //     return (
  //       <ItemDevice
  //         key={item.snCode}
  //         item={item}
  //         checked={currentDevice.current?.snCode === item.snCode}
  //         onSelect={selectDevice}
  //       />
  //     );
  //   });
  //   return Items;
  // };

  // 顶部切换学生组件
  const studentChange = (
    <div onClick={() => setShowMenu(true)} styleName="nav-student-select">
      <div styleName="nav-student-text">{deviceInfo?.userName}</div>
      <i
        styleName="student-icon"
        className="iconfont icon-sanjiaojiantou-zuo"
      />
    </div>
  );

  // 跳转到常去地点页
  const toSettingPlace = () => {
    if (deviceInfo.status === deviceStatus.locked) {
      return ConfirmModal.show({
        title: '设备已锁定',
        text:
          '设备插入的SIM卡不是服务商提供，因此设备已被锁定，解锁请插入服务商提供的SIM卡，并确认SIM卡可正常使用。',
        confirmBtnText: '我知道了',
      });
    }
    if (deviceInfo.status === deviceStatus.forbid) {
      return ConfirmModal.show({
        title: '设备已禁用',
        text: '设备已被服务商禁用，请联系服务商解除。',
        confirmBtnText: '我知道了',
        confirmBtnStyle: { backgroundColor: '#F2F4F7', color: '#55585C' },
      });
    }

    FridayService.addEvent(
      'real_time_location_more_location_settings_frequent_places_display',
      { source: '设置常去地点提示浮窗' },
    );
    setTimeout(() => {
      BrowserHistory.push(
        `${PUBLIC_URL}/locationSettingFrequentedPlace?snCode=${currentDevice.current.snCode}&company=${currentDevice.current.company}`,
      );
    }, 100);
  };

  //监听app传回的active 和visible事件（处理在app切入到前台、app 定位页tab显示时 这两种情况），刷新当前设备页数据
  const subscribe = async () => {
    const np = await getNativeProxySington('seewoCube').catch(e => {});
    np.inject({
      name: 'appActionHandler',
      func: (data: any) => {
        if (
          data.action === 'action_webview_active' ||
          data.action === 'action_webview_visible'
        ) {
          setDeviceDetail(null);
          getDeviceInfo();
        }
      },
    });
  };

  const toScanCode = () => {
    FridayService.addEvent('bind_device_scan_self_show', { source: '定位页' });
    if (isCubeApp()) {
      NativeInterface.jumpNative({ path: '/scan', extras: {} });
    } else {
      BrowserHistory.push(`${PUBLIC_URL}${paths.scan}`);
    }
  };

  const closeLocationPopWindow = () => {
    setDisplayLocationPopWindow(false);
    getDeviceDetail(currentDevice.current);
  };
  const getMessageInfo = async () => {
    const result = await Http.post(Api.GetNotificationNotReadMessageNums, {
      uid: userStore.userInfo.uid,
    });
    const nums = Number(result.data);
    setMessageNum(nums);
  };

  const onTouchStartMap = () => {
    CardComponent.current.hiddenPopUp();
  };

  useEffect(() => {
    FridayService.addEvent('real_time_positioning_self_show', {
      source: params.current.sncode ? '我的设备' : '设备定位',
    });
    getDeviceInfo();
    getMessageInfo();
    if (isCubeApp()) {
      subscribe();
    }
    return () => {
      clearTimer();
      intervalTimer.current && clearInterval(intervalTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let contentTextDesc = '';
  if (percentCount > 0 && percentCount <= 30) {
    contentTextDesc = `发送定位任务${percentCount}%`;
  } else if (percentCount > 30 && percentCount <= 60) {
    contentTextDesc = `设备定位中${percentCount}%`;
  } else if (percentCount > 60 && percentCount <= 99) {
    contentTextDesc = `获取定位中${percentCount}%`;
  }

  const toNoticeList = () => {
    FridayService.addEvent('home_notice_entrance_click');
    BrowserHistory.push(`${PUBLIC_URL}${paths.notificationGroupList}`);
  };

  if (devices !== null && !devices.length) {
    return <Empty />;
  }

  const renderNavRightComponent = () => {
    let Icon = null;
    if (isCubeApp()) {
      Icon = (
        <i
          className="iconfont icon-saoyisao"
          styleName="nav-icon"
          onClick={toScanCode}
          style={{ marginRight: '15px' }}
        />
      );
    } else {
      let messageIcon = (
        <>
          {messageNum > 0 ? (
            <div className="flex flex-center" styleName="message-red-notice">
              {messageNum}
            </div>
          ) : null}
        </>
      );
      if (messageNum > 9 && messageNum <= 99) {
        messageIcon = (
          <div className="flex flex-center" styleName="message-red-notice">
            <span style={{ transform: 'scale(.7)' }}> {messageNum}</span>
          </div>
        );
      } else if (messageNum > 99) {
        messageIcon = (
          <div className="flex flex-center" styleName="message-red-notice">
            <i className="iconfont icon-gengduo" />
          </div>
        );
      }
      Icon = (
        <div className="flex flex-center" styleName="nav-btn">
          <div
            className="flex flex-center"
            styleName="icon-message-wrapper"
            style={{ marginRight: '15px' }}
            onClick={toNoticeList}
          >
            <i className="iconfont icon-message" styleName="nav-icon" />
            {messageIcon}
          </div>
          <i
            className="iconfont icon-saoyisao"
            styleName="nav-icon"
            onClick={toScanCode}
          />
        </div>
      );
    }
    return Icon;
  };

  const hideBackButton = params.current.hideBackButton
    ? params.current.hideBackButton === 'true'
    : userStore.userInfo.loginFromUC;
  return (
    <div style={{ width: '100%', height: document.body.clientHeight }}>
      <NavBar
        TitleComponent={studentChange}
        hideBackButton={hideBackButton}
        RightBtnComponent={renderNavRightComponent()}
      />
      {errInfo ? (
        <Tips title={errInfo?.title} clickEvent={errInfo?.clickEvent || null} />
      ) : null}

      <BaseMap
        ref={mapRef}
        center={center}
        zoom={18}
        onTouchStart={onTouchStartMap}
      >
        <CustomOverlay position={center}>
          <div className="flex" styleName="avatar-info">
            {contentText || contentTextDesc ? (
              <div className="flex flex-center" styleName="avatar-label">
                {contentText || contentTextDesc}
              </div>
            ) : null}
            {devicePosition ? (
              <div styleName="avatar-wrapper">
                <div styleName="avatar">
                  <img
                    styleName="avatar-img"
                    src={
                      currentDevice.current?.bindUserHeadImageUrl ||
                      defaultAvatar
                    }
                    alt=""
                  />
                </div>
              </div>
            ) : null}
          </div>
        </CustomOverlay>
      </BaseMap>
      {devices?.length ? (
        <div
          styleName={`${
            params.current.hideBackButton &&
            params.current.hideBackButton === 'true'
              ? 'bg-container-small'
              : 'bg-container'
          }`}
        >
          <div className="flex" styleName="container">
            <div styleName="controls" className="flex">
              {/* <div styleName="avatars" className="flex">
                {renderDevices()}
              </div> */}
              <div
                className="flex flex-center"
                styleName="icon-qinqingdianhua"
                onClick={call}
              >
                <i className="iconfont icon-qinqingdianhua" />
              </div>
              {showPlaceHint && (
                <div styleName="setting-place" onClick={toSettingPlace}>
                  <i
                    styleName="place-icon"
                    className="iconfont icon-position"
                  />
                  <div>设置常去地点，定位更准</div>
                  <i
                    styleName="arrow-icon"
                    className="iconfont icon-sanjiaojiantou-zuo"
                  />
                </div>
              )}
            </div>
            <Card
              devicePosition={devicePosition}
              currentDevice={currentDevice.current}
              deviceInfo={deviceInfo}
              onRefresh={onRefresh}
              animation={showAnimation}
              ref={CardComponent}
            />
          </div>
        </div>
      ) : null}
      {displayLocationPopWindow && !hasRemindAuth ? (
        <LocationPopWindow
          deviceInfo={deviceSNInfo}
          onClose={() => setDisplayLocationPopWindow(false)}
          onConfirm={closeLocationPopWindow}
        />
      ) : null}
      <DeviceRemindAuth
        onClose={() => setRemindAuth(false)}
        onShow={() => setRemindAuth(true)}
      />
      {showMenu ? (
        <ChildrenSelectMenu
          childrenInfo={devices}
          currentDevice={currentDevice.current}
          onClose={() => setShowMenu(false)}
          onSelect={Children => selectDevice(Children)}
        />
      ) : null}
    </div>
  );
};

export default Location;
